import BaseModel from './BaseModel';
import Model from './Model';
import Reference from './Reference';
import Tag from './Tag';
import ScreeningProgressSummary from './ScreeningProgressSummary';
import ScreeningOutcomeSummary from './ScreeningOutcomeSummary';
import ScreeningOutcome from './ScreeningOutcome';
import ReviewMember from './ReviewMember';
import ReviewMemberInvite from './ReviewMemberInvite';
import ReviewCriteria from './ReviewCriteria';
import RemainingRelevantAnalysisDto from './RemainingRelevantAnalysisDto';
import ReferenceFile from './ReferenceFile';
import { createDataExtractionField } from './DataExtractionField';
import DataExtractionFieldDraft from './DataExtractionFieldDraft';
import DataExtractionProgressSummary from './DataExtractionProgressSummary';
import {
  CriteriaType,
  ReviewMemberPermission,
  ReviewMemberRole,
  RelevanceAssessment,
  ScreeningAdviceState,
} from '@/enums';
import PrismaStats from './PrismaStats';
import moment from 'moment';
import UploadGroupDto from './UploadGroupDto';
import TrainingStats from './TrainingStats';
import AutoScreeningJob from './AutoScreeningJob';
import { deepEqualExcludingId } from '@/modules/compareContents';

export default class Review extends BaseModel {
  constructor(data = {}) {
    super(Review, data);
    this.id = data.id;
    this.title = data.title;
    this.livingReview = data.livingReview || false;
    this.screeningVotesRequired = data.screeningVotesRequired || 2;
    this.fullTextReviewVotesRequired =
      data.fullTextReviewVotesRequired || 2;
    this.dataExtractionReportsRequired =
      data.dataExtractionReportsRequired || 2;
    this.unresolvedDuplicatesCount = data.unresolvedDuplicatesCount;
    this.anyRemainingRelevant = data.anyRemainingRelevant;
    this.remainingRelevantAnalysis =
      data.remainingRelevantAnalysis &&
      new RemainingRelevantAnalysisDto(
        data.remainingRelevantAnalysis
      );
    this.latestModel = data.latestModel
      ? new Model(data.latestModel)
      : null;
    this.activeJob =
      data.activeJob && new AutoScreeningJob(data.activeJob);
    this.trainingStats = data.trainingStats
      ? new TrainingStats(data.trainingStats)
      : null;
    this.unresolvedReferenceDuplicates = data.unresolvedReferenceDuplicates
      ? data.unresolvedReferenceDuplicates.map((group) =>
          group.map((reference) => new Reference(reference))
        )
      : [];
    this.screeningProgressSummary = data.screeningProgressSummary
      ? new ScreeningProgressSummary(data.screeningProgressSummary)
      : null;
    this.screeningOutcomeSummary = data.screeningOutcomeSummary
      ? new ScreeningOutcomeSummary(data.screeningOutcomeSummary)
      : null;
    this.mostRecentScreeningOutcomes = data.mostRecentScreeningOutcomes
      ? data.mostRecentScreeningOutcomes.map(
          (data) => new ScreeningOutcome(data)
        )
      : null;
    this.reviewMembers = data.reviewMembers
      ? data.reviewMembers.map((member) => new ReviewMember(member))
      : [];
    this.leadReviewer = data.leadReviewer
      ? new ReviewMember(data.leadReviewer)
      : null;
    this.reviewers = data.reviewers
      ? data.reviewers.map((reviewer) => new ReviewMember(reviewer))
      : null;
    this.currentReviewMember =
      data.currentReviewMember &&
      new ReviewMember(data.currentReviewMember);
    this.invites = data.invites
      ? data.invites.map((invite) => new ReviewMemberInvite(invite))
      : null;
    this.viewers = data.reviewers
      ? data.reviewers.map((reviewer) => new ReviewMember(reviewer))
      : null;
    this.reviewCriteria = data.reviewCriteria
      ? data.reviewCriteria.map(
          (criteria) => new ReviewCriteria(criteria)
        )
      : null;
    this.fullTextReviewScreeningProgressSummary = data.fullTextReviewScreeningProgressSummary
      ? new ScreeningProgressSummary(
          data.fullTextReviewScreeningProgressSummary
        )
      : null;
    this.fullTextReviewScreeningOutcomeSummary = data.fullTextReviewScreeningOutcomeSummary
      ? new ScreeningOutcomeSummary(
          data.fullTextReviewScreeningOutcomeSummary
        )
      : null;
    this.dataExtractionFields =
      data.dataExtractionFields?.map(createDataExtractionField) ?? [];
    this.dataExtractionFieldDrafts =
      data.dataExtractionFieldDrafts?.map(
        (draft) => new DataExtractionFieldDraft(draft)
      ) ?? [];
    this.dataExtractionProgressSummary = data.dataExtractionProgressSummary
      ? new DataExtractionProgressSummary(
          data.dataExtractionProgressSummary
        )
      : null;
    this.tags = data.tags?.map((tagData) => new Tag(tagData)) ?? [];
    this.prismaStats = new PrismaStats(data.prismaStats);
    this.dataExtractionTemplatePublishedAt = data.dataExtractionTemplatePublishedAt
      ? moment(data.dataExtractionTemplatePublishedAt)
      : null;
    this.uploads =
      data.uploads?.map((upload) => new ReferenceFile(upload)) ?? [];
    this.archived = data.archived || false;
    this.reviewType = data.reviewType;
    this.uniqueReferencesCount = data.uniqueReferencesCount;
    this.resolvedDuplicatesCount = data.resolvedDuplicatesCount;
    this.resolvedNotDuplicatesCount = data.resolvedNotDuplicatesCount;
    this.duplicatesAutoResolvedCount =
      data.duplicatesAutoResolvedCount;
    this.duplicatesManuallyResolvedCount =
      data.duplicatesManuallyResolvedCount;
    this.potentialDuplicatesCount = data.potentialDuplicatesCount;
  }

  creationData() {
    return {
      id: this.id,
      title: this.title,
      reviewType: this.reviewType,
      livingReview: this.livingReview,
    };
  }

  totalReferences() {
    const summary = this.screeningProgressSummary;
    return (
      summary.unscreenedCount +
      summary.partiallyScreenedCount +
      summary.conflictedScreenCount +
      summary.screenedCount
    );
  }

  // reviewCriteria is null when the page's query did not select it, so treat that the
  // same as a review with no criteria rather than throwing mid-render.
  exclusionCriteria() {
    return (this.reviewCriteria ?? []).filter(
      (criteria) => criteria.criteriaType === CriteriaType.EXCLUSION
    );
  }

  extractionTemplateIsDraft() {
    const fields = this.dataExtractionFields;
    const draftFields = this.dataExtractionFieldDrafts;

    // If user creates then deletes a field without publishing, we should treat that as unchanged
    const draftsTrimmed = draftFields.filter(
      (draft) => draft.existingField || draft.updatedField
    );
    if (draftsTrimmed.length !== fields.length) {
      return true;
    }

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const draft = draftsTrimmed[i];
      if (draft.modified) {
        const { existingField, updatedField } = draft;
        if (!existingField || !updatedField) {
          return true;
        }
        return !deepEqualExcludingId(existingField, updatedField);
      }
      if (draft.existingField?.id !== field.id) {
        return true;
      }
    }

    return false;
  }

  uploadGroups() {
    const uploadGroupsById = {};
    const uploadGroups = [];
    this.uploads.forEach((upload) => {
      if (!uploadGroupsById[upload.uploadGroupId]) {
        const newUploadGroup = new UploadGroupDto({
          id: upload.uploadGroupId,
          referenceFiles: [],
        });
        uploadGroupsById[upload.uploadGroupId] = newUploadGroup;
        uploadGroups.push(newUploadGroup);
      }
      uploadGroupsById[upload.uploadGroupId].referenceFiles.push(
        upload
      );
    });

    return uploadGroups;
  }

  /**
   * Whether the current user may make changes to this review, mirroring the server's
   * write-access rule: lead reviewers and reviewers may, viewers and non-members may not.
   * Super admins pass server-side regardless of membership.
   */
  currentUserCanEditReview() {
    const role = this.currentReviewMember?.memberRole;
    return (
      role === ReviewMemberRole.LEAD_REVIEWER ||
      role === ReviewMemberRole.REVIEWER ||
      Boolean(this.currentReviewMember?.researcher?.isSuperAdmin)
    );
  }

  currentUserIsScreeningConflictResolver() {
    return this.isScreeningConflictResolver(this.currentReviewMember);
  }

  currentUserIsFullTextReviewConflictResolver() {
    return this.isFullTextReviewConflictResolver(
      this.currentReviewMember
    );
  }

  currentUserIsDataExtractionConflictResolver() {
    return this.isDataExtractionConflictResolver(
      this.currentReviewMember
    );
  }

  isScreeningConflictResolver(reviewMember) {
    return this.reviewMemberHasPermission(
      reviewMember,
      ReviewMemberPermission.RESOLVE_SCREENING_CONFLICT
    );
  }

  isFullTextReviewConflictResolver(reviewMember) {
    return this.reviewMemberHasPermission(
      reviewMember,
      ReviewMemberPermission.RESOLVE_FULL_TEXT_REVIEW_CONFLICT
    );
  }

  isDataExtractionConflictResolver(reviewMember) {
    return this.reviewMemberHasPermission(
      reviewMember,
      ReviewMemberPermission.RESOLVE_DATA_EXTRACTION_CONFLICT
    );
  }

  reviewMemberHasPermission(reviewMember, permission) {
    const conflictResolverIds = this.getReviewMembersByPermission(
      permission
    ).map((member) => member.id);

    if (conflictResolverIds.length === 0) return true;
    if (reviewMember.researcher.isSuperAdmin) return true;
    return conflictResolverIds.includes(reviewMember.id);
  }

  researcherHasPermission(researcher, permission) {
    const conflictResolverIds = this.getReviewMembersByPermission(
      permission
    ).map((member) => member.researcher.id);

    if (conflictResolverIds.length === 0) return true;
    if (researcher.isSuperAdmin) return true;
    return conflictResolverIds.includes(researcher.id);
  }

  researcherIsTrustedScreeningReviewer(researcher) {
    return this.researcherHasPermission(
      researcher,
      ReviewMemberPermission.TRUSTED_SCREENING_VOTE
    );
  }

  researcherIsTrustedFullTextReviewer(researcher) {
    return this.researcherHasPermission(
      researcher,
      ReviewMemberPermission.TRUSTED_FULL_TEXT_REVIEW
    );
  }

  researcherIsTrustedDataExtractionReviewer(researcher) {
    return this.researcherHasPermission(
      researcher,
      ReviewMemberPermission.TRUSTED_DATA_EXTRACTION
    );
  }

  researcherIsAdmin(researcher) {
    return (
      this.leadReviewer.researcher.id === researcher.id ||
      researcher.isSuperAdmin
    );
  }

  getTrustedScreeningReviewers() {
    return this.getReviewMembersByPermission(
      ReviewMemberPermission.TRUSTED_SCREENING_VOTE
    );
  }

  getTrustedFullTextReviewers() {
    return this.getReviewMembersByPermission(
      ReviewMemberPermission.TRUSTED_FULL_TEXT_REVIEW
    );
  }

  getTrustedDataExtractionReviewers() {
    return this.getReviewMembersByPermission(
      ReviewMemberPermission.TRUSTED_DATA_EXTRACTION
    );
  }

  researcherIsScreeningConflictResolver(researcher) {
    return this.researcherHasPermission(
      researcher,
      ReviewMemberPermission.RESOLVE_SCREENING_CONFLICT
    );
  }

  researcherIsFullTextReviewConflictResolver(researcher) {
    return this.researcherHasPermission(
      researcher,
      ReviewMemberPermission.RESOLVE_FULL_TEXT_REVIEW_CONFLICT
    );
  }

  researcherIsDataExtractionConflictResolver(researcher) {
    return this.researcherHasPermission(
      researcher,
      ReviewMemberPermission.RESOLVE_DATA_EXTRACTION_CONFLICT
    );
  }

  getReviewMembersByPermission(permission) {
    return this.reviewMembers.filter((member) =>
      member.memberPermissions.includes(permission)
    );
  }

  screeningAdviceState() {
    if (this.isScreeningComplete()) {
      return ScreeningAdviceState.COMPLETE;
    }
    if (this.shouldStopScreening()) {
      return ScreeningAdviceState.STOP;
    }
    return ScreeningAdviceState.CONTINUE;
  }

  shouldStopScreening() {
    const remainingReferenceCount =
      this.screeningProgressSummary?.totalRemaining() ?? 0;
    return (
      remainingReferenceCount > 0 &&
      !this.anyRemainingRelevant &&
      this.negativeStreak() >= 40
    );
  }

  negativeStreak() {
    const outcomes = this.mostRecentScreeningOutcomes ?? [];
    let count = 0;
    for (let outcome of outcomes) {
      if (outcome.outcome !== RelevanceAssessment.NOT_RELEVANT) {
        return count;
      }
      count++;
    }
    return count;
  }

  isScreeningComplete() {
    return (
      this.screeningProgressSummary?.totalRemaining() === 0 &&
      this.screeningOutcomeSummary?.totalOutcomes() > 0
    );
  }

  isFullTextReviewComplete() {
    return (
      this.fullTextReviewScreeningProgressSummary?.totalRemaining() ===
        0 &&
      this.fullTextReviewScreeningOutcomeSummary?.totalOutcomes() > 0
    );
  }

  isDataExtractionComplete() {
    return (
      this.dataExtractionProgressSummary?.totalRemaining() === 0 &&
      this.dataExtractionProgressSummary?.extractedCount > 0
    );
  }
}
